import { ROUTES } from '@shared/utils';
import type { SessionStatus } from '../types';

const AUTH_PENDING_CALLBACK_KEY = 'auth:pending-callback';

function getPlayRouteFallbackPath(callbackUrl: string): string | null {
  if (!callbackUrl) {
    return null;
  }

  const path = callbackUrl.split('?')[0] ?? callbackUrl;

  if (!path.endsWith('/play')) {
    return null;
  }

  if (path.startsWith(ROUTES.LIVE_CASINO.DISCOVERY)) {
    return ROUTES.LIVE_CASINO.DISCOVERY;
  }

  if (path.startsWith(ROUTES.CASINO.DISCOVERY)) {
    return ROUTES.CASINO.DISCOVERY;
  }

  return null;
}

function setAuthPendingCallback(callbackUrl: string): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }

  const lobbyPath = getPlayRouteFallbackPath(callbackUrl);
  if (lobbyPath === null) {
    return;
  }

  sessionStorage.setItem(AUTH_PENDING_CALLBACK_KEY, lobbyPath);
}

function clearAuthPendingCallback(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }

  sessionStorage.removeItem(AUTH_PENDING_CALLBACK_KEY);
}

function hasAuthPendingCallback(callbackUrl: string): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }

  const lobbyPath = getPlayRouteFallbackPath(callbackUrl);
  if (lobbyPath === null) {
    return false;
  }

  return sessionStorage.getItem(AUTH_PENDING_CALLBACK_KEY) === lobbyPath;
}

function consumeAuthPendingCallback(callbackUrl: string): boolean {
  if (!hasAuthPendingCallback(callbackUrl)) {
    return false;
  }

  clearAuthPendingCallback();
  return true;
}

function buildCallbackUrlFromWindow(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  const pathname = window.location.pathname ?? '';
  const search = window.location.search ?? '';
  return search ? `${pathname}${search}` : pathname;
}

function getStoredPendingLobbyPath(): string | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }

  const storedLobby = sessionStorage.getItem(AUTH_PENDING_CALLBACK_KEY);
  if (storedLobby !== ROUTES.CASINO.DISCOVERY && storedLobby !== ROUTES.LIVE_CASINO.DISCOVERY) {
    return null;
  }

  return storedLobby;
}

function clearAuthPendingCallbackIfOnMatchingLobby(pathname: string): void {
  const storedLobby = getStoredPendingLobbyPath();
  if (storedLobby === null) {
    return;
  }

  if (pathname === ROUTES.CASINO.DISCOVERY || pathname === ROUTES.LIVE_CASINO.DISCOVERY) {
    if (storedLobby === pathname) {
      clearAuthPendingCallback();
    }
  }
}

function isBackForwardNavigation(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  return navigationEntry?.type === 'back_forward';
}

const RECENT_DOCUMENT_LOAD_MAX_AGE_MS = 3000;

function isRecentDocumentLoad(maxAgeMs = RECENT_DOCUMENT_LOAD_MAX_AGE_MS): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  if (!navigationEntry) {
    return false;
  }

  return performance.now() - navigationEntry.startTime <= maxAgeMs;
}

type ShouldHandlePendingAuthReturnOptions = {
  hasInitiatedSignIn?: boolean;
  isPageshowReturn?: boolean;
  recentDocumentLoadMaxAgeMs?: number;
};

function shouldHandlePendingAuthReturn(pathname: string, options: ShouldHandlePendingAuthReturnOptions = {}): boolean {
  const { hasInitiatedSignIn = false, isPageshowReturn = false, recentDocumentLoadMaxAgeMs = RECENT_DOCUMENT_LOAD_MAX_AGE_MS } = options;

  if (isPageshowReturn || isBackForwardNavigation()) {
    return true;
  }

  if (hasInitiatedSignIn) {
    return false;
  }

  if (getStoredPendingLobbyPath() === null) {
    return false;
  }

  if (!isRecentDocumentLoad(recentDocumentLoadMaxAgeMs)) {
    return false;
  }

  const callbackUrl = buildCallbackUrlFromWindow();
  const playRouteLobby = getPlayRouteFallbackPath(callbackUrl || pathname);

  if (playRouteLobby !== null) {
    return hasAuthPendingCallback(callbackUrl || pathname);
  }

  return pathname !== getStoredPendingLobbyPath();
}

type SyncPendingAuthReturnStateOptions = {
  handlePlayRouteReturn?: boolean;
};

function syncPendingAuthReturnState(sessionStatus: SessionStatus, pathname: string, options: SyncPendingAuthReturnStateOptions = {}): boolean {
  const handlePlayRouteReturn = options.handlePlayRouteReturn ?? true;

  if (sessionStatus === 'authenticated') {
    clearAuthPendingCallback();
    return false;
  }

  if (sessionStatus === 'loading' || sessionStatus === 'uninitialised') {
    return false;
  }

  clearAuthPendingCallbackIfOnMatchingLobby(pathname);

  if (!handlePlayRouteReturn) {
    return false;
  }

  const storedLobby = getStoredPendingLobbyPath();
  if (storedLobby === null) {
    return false;
  }

  const callbackUrl = buildCallbackUrlFromWindow();
  const playRouteLobby = getPlayRouteFallbackPath(callbackUrl);

  if (playRouteLobby !== null && storedLobby === playRouteLobby) {
    if (!consumeAuthPendingCallback(callbackUrl)) {
      return false;
    }

    window.location.replace(storedLobby);
    return true;
  }

  if (playRouteLobby === null && pathname !== storedLobby) {
    clearAuthPendingCallback();
    window.location.replace(storedLobby);
    return true;
  }

  return false;
}

export {
  AUTH_PENDING_CALLBACK_KEY,
  buildCallbackUrlFromWindow,
  clearAuthPendingCallback,
  clearAuthPendingCallbackIfOnMatchingLobby,
  consumeAuthPendingCallback,
  getPlayRouteFallbackPath,
  getStoredPendingLobbyPath,
  hasAuthPendingCallback,
  isBackForwardNavigation,
  isRecentDocumentLoad,
  setAuthPendingCallback,
  shouldHandlePendingAuthReturn,
  syncPendingAuthReturnState,
};
